import { listFileExtractions, type CoworkExtraction, type CoworkFile, type CoworkFileExtraction } from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { createEffect, createMemo, createResource, For, lazy, on, onCleanup, onMount, Show, Suspense } from "solid-js"
import { createStore } from "solid-js/store"
import {
  AccountingRecordFacts,
  ExtractedFields as ExtractedValue,
  ExtractionFieldLabels,
} from "./extracted-fields-table"

const AccountingExtractionData = lazy(() =>
  import("@/pages/session/v2/cowork-files-panel").then((module) => ({ default: module.AccountingExtractionData })),
)

type ExtractionSnapshot = { scope: string; rows: CoworkFileExtraction[]; failed: boolean }

const outcomeMetadata = new Set([
  "disposition",
  "filename",
  "source_id",
  "file_id",
  "source_refs",
  "evidence_refs",
  "status",
  "identity",
  "summary",
  "limitations",
  "issues",
  "source_extraction_id",
  "extraction_id",
  "field_contract_version",
])

function hasValue(value: unknown): boolean {
  if (value == null) return false
  if (typeof value === "string") return value.trim().length > 0
  if (Array.isArray(value)) return value.some(hasValue)
  if (typeof value === "object") return Object.values(value).some(hasValue)
  return true
}

function hasExtractedFields(record: CoworkFileExtraction) {
  if (record.field_view?.has_source_fields === false) return false
  return Object.entries(record.result_json).some(([key, value]) => !outcomeMetadata.has(key) && hasValue(value))
}

function displayedExtractionFields(value: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(value).filter(([key]) => key !== "identity" && key !== "status"))
}

function sourceSupportingFields(value: Record<string, unknown>) {
  const primary = new Set([
    "document_fields",
    "fields",
    "key_values",
    "invoice",
    "receipt",
    "documents",
    "accounting_records",
    "normalized",
    "analysis",
    "field_contract_version",
  ])
  return Object.fromEntries(Object.entries(displayedExtractionFields(value)).filter(([key]) => !primary.has(key)))
}

function currentExtraction(records: CoworkFileExtraction[]) {
  return records.find((record) => !record.superseded && record.current_content !== false) ?? records[0]
}

export function FileExtractedData(props: { file: CoworkFile; periodID?: string | null; active?: boolean }) {
  const language = useLanguage()
  let container: HTMLElement | undefined
  const scope = () =>
    JSON.stringify([
      props.file.company_id,
      props.file.project_id,
      props.file.id,
      props.periodID ?? null,
      props.file.updated_at,
    ])
  const [records, actions] = createResource<ExtractionSnapshot, string>(scope, async (key, previous) => {
    const file = props.file
    const periodID = props.periodID
    try {
      const values = await listFileExtractions(file.id, { companyID: file.company_id, periodID })
      const existing = previous.value?.scope === key ? previous.value.rows : []
      const byID = new Map(existing.map((record) => [record.id, record]))
      const rows = values
        .filter(
          (record) =>
            record.file_id === file.id &&
            record.company_id === file.company_id &&
            record.project_id === file.project_id &&
            (!periodID || (record.period_id ?? record.workflow_id) === periodID),
        )
        .map((record) => {
          const saved = byID.get(record.id)
          // Preserve unchanged results so polling does not remount tables or reset their scroll position.
          return saved && JSON.stringify(saved) === JSON.stringify(record) ? saved : record
        })
      return {
        scope: key,
        rows: rows.length === existing.length && rows.every((row, index) => row === existing[index]) ? existing : rows,
        failed: false,
      }
    } catch {
      return { scope: key, rows: [], failed: true }
    }
  })
  const snapshot = () => (records.latest?.scope === scope() ? records.latest : undefined)
  const refresh = () => {
    if (props.active === false || document.visibilityState !== "visible" || records.loading) return
    if (!container?.getClientRects().length) return
    void actions.refetch()
  }
  createEffect(
    on(
      () => props.active,
      () => refresh(),
      { defer: true },
    ),
  )
  onMount(() => {
    const interval = window.setInterval(refresh, 5000)
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", refresh)
    onCleanup(() => {
      window.clearInterval(interval)
      window.removeEventListener("focus", refresh)
      document.removeEventListener("visibilitychange", refresh)
    })
  })
  return (
    <section ref={(element) => (container = element)} class="p-4 text-[12px]">
      <div class="mb-4 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h2 class="text-[14px] font-semibold">{language.t("cowork.enterprise.data.extracted")}</h2>
          <p class="mt-1 break-all text-v2-text-text-muted">{props.file.filename}</p>
        </div>
        <button
          type="button"
          class="shrink-0 rounded-md border border-v2-border-border-base px-3 py-1.5"
          disabled={records.loading}
          onClick={() => void actions.refetch()}
        >
          {language.t("cowork.activity.refresh")}
        </button>
      </div>
      <Show
        when={!snapshot()?.failed}
        fallback={
          <div role="alert" class="text-v2-state-fg-danger">
            {language.t("common.requestFailed")}
            <button type="button" class="ml-3 underline" onClick={() => void actions.refetch()}>
              {language.t("cowork.enterprise.workflows.reporting.retry")}
            </button>
          </div>
        }
      >
        <Show when={snapshot()} fallback={<p role="status">{language.t("cowork.files.panel.loadingPreview")}</p>}>
          <Show
            when={snapshot()?.rows.length}
            fallback={<p>{language.t("cowork.enterprise.data.extractedEmptyTitle")}</p>}
          >
            <ExtractionHistory records={snapshot()?.rows ?? []} file={props.file} />
          </Show>
        </Show>
      </Show>
    </section>
  )
}

function ExtractionHistory(props: { records: CoworkFileExtraction[]; file: CoworkFile }) {
  const language = useLanguage()
  let schemaSelect: HTMLSelectElement | undefined
  let historySelect: HTMLSelectElement | undefined
  let alive = true
  onCleanup(() => {
    alive = false
  })
  const [state, setState] = createStore({ schema: undefined as string | undefined, recordID: "" })
  const schemas = createMemo(() => [...new Set(props.records.map((record) => record.schema_name ?? ""))])
  const sorted = createMemo(() =>
    props.records.toSorted((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at) || b.id.localeCompare(a.id)),
  )
  const preferredSchema = createMemo(() => {
    const candidates = schemas().map((name) =>
      currentExtraction(sorted().filter((record) => (record.schema_name ?? "") === name)),
    )
    const kind = { source: 0, workflow: 1, report: 2 }
    // Compare the latest applicable result per schema; never hide a newer failure behind an older success.
    candidates.sort(
      (a, b) =>
        Number(a.superseded || a.current_content === false) - Number(b.superseded || b.current_content === false) ||
        Number(b.schema_name === "file.extracted_data") - Number(a.schema_name === "file.extracted_data") ||
        Number(hasExtractedFields(b)) - Number(hasExtractedFields(a)) ||
        kind[a.record_type] - kind[b.record_type] ||
        Date.parse(b.created_at) - Date.parse(a.created_at),
    )
    return candidates[0]?.schema_name ?? ""
  })
  const schema = () =>
    state.schema !== undefined && schemas().includes(state.schema) ? state.schema : preferredSchema()
  const history = createMemo(() => sorted().filter((record) => (record.schema_name ?? "") === schema()))
  const selected = createMemo(
    () => history().find((record) => record.id === state.recordID) ?? currentExtraction(history()),
  )
  createEffect(
    on(
      () => [schemas(), history(), schema(), selected()?.id],
      () => {
        // Apply controlled values after newly fetched options have been inserted into the DOM.
        queueMicrotask(() => {
          if (!alive) return
          if (schemaSelect) schemaSelect.value = schema()
          if (historySelect) historySelect.value = selected()?.id ?? ""
        })
      },
    ),
  )
  const recordLabel = (record: CoworkFileExtraction) =>
    [
      new Date(record.created_at).toLocaleString(),
      language.t(
        record.record_type === "source"
          ? "cowork.enterprise.files.source"
          : record.record_type === "report"
            ? "cowork.pipeline.report"
            : "cowork.project.type.workflow",
      ),
      record.revision ? language.t("error.page.version", { version: record.revision }) : "",
      record.workflow_run_id || record.id,
    ]
      .filter(Boolean)
      .join(" · ")
  return (
    <>
      <div class="mb-4 grid gap-3">
        <label class="grid gap-1">
          {language.t("cowork.enterprise.files.type")}
          <select
            ref={(element) => (schemaSelect = element)}
            class="min-w-0 rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-2"
            value={schema()}
            onChange={(event) => setState({ schema: event.currentTarget.value, recordID: "" })}
          >
            <For each={schemas()}>{(name) => <option value={name}>{name || language.t("common.unknown")}</option>}</For>
          </select>
        </label>
        <label class="grid gap-1">
          {language.t("cowork.enterprise.workflows.reporting.history")}
          <select
            ref={(element) => (historySelect = element)}
            class="min-w-0 rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-2"
            value={selected()?.id}
            onChange={(event) => setState("recordID", event.currentTarget.value)}
          >
            <For each={history()}>{(record) => <option value={record.id}>{recordLabel(record)}</option>}</For>
          </select>
        </label>
      </div>
      <Show when={selected()} keyed>
        {(record) => <ExtractionResult record={record} file={props.file} />}
      </Show>
    </>
  )
}

function ExtractionResult(props: { record: CoworkFileExtraction; file: CoworkFile }) {
  const language = useLanguage()
  const record = props.record
  const statusKeys = {
    completed: "cowork.enterprise.workflows.reporting.extraction.completed",
    partial: "cowork.enterprise.workflows.reporting.extraction.partial",
    failed: "cowork.enterprise.workflows.reporting.extraction.failed",
    unreadable: "cowork.enterprise.workflows.reporting.extraction.unreadable",
    unsupported: "cowork.enterprise.workflows.reporting.extraction.unsupported",
  } as const
  const statusKey = () => record.status === "completed" && !hasExtractedFields(record)
    ? undefined : statusKeys[record.status as keyof typeof statusKeys]
  return (
    <div class="space-y-4">
      <Show when={statusKey()}>{(key) => <p class="font-medium">{language.t(key())}</p>}</Show>
      <Show when={record.summary}>
        <p class="whitespace-pre-wrap break-words">{record.summary}</p>
      </Show>
      <Show when={record.superseded || record.current_content === false}>
        <p class="rounded-md border border-v2-border-border-base p-3">
          {language.t("cowork.files.extraction.previousVersion")}
        </p>
      </Show>
      <Show when={record.limitations.length > 0}>
        <section>
          <h3 class="mb-2 font-semibold">{language.t("cowork.accounting.overview.warnings")}</h3>
          <ExtractedValue value={record.limitations} />
        </section>
      </Show>
      <Show
        when={hasExtractedFields(record)}
        fallback={
          <>
            <div class="space-y-1 rounded-md border border-v2-border-border-base p-3">
              <p>{language.t("cowork.enterprise.data.extractedEmptyTitle")}</p>
              <p class="text-v2-text-text-muted">{language.t("cowork.enterprise.data.extractedEmptyDescription")}</p>
            </div>
            <details class="rounded-md border border-v2-border-border-base p-3">
              <summary class="cursor-pointer">{language.t("cowork.enterprise.audit.viewDetails")}</summary>
              <div class="mt-3">
                <ExtractedValue value={displayedExtractionFields(record.result_json)} />
              </div>
            </details>
          </>
        }
      >
        <ExtractionDataFields record={record} files={[props.file]} />
      </Show>
      <Show when={hasValue(record.provenance)}>
        <details class="rounded-md border border-v2-border-border-base p-3">
          <summary class="cursor-pointer font-medium">{language.t("cowork.enterprise.files.source")}</summary>
          <div class="mt-3">
            <ExtractedValue value={record.provenance} discloseSupporting={false} />
          </div>
        </details>
      </Show>
    </div>
  )
}

export function ExtractionDataFields(props: { record: CoworkExtraction; files: CoworkFile[] }) {
  const language = useLanguage()
  const fieldView = () =>
    props.record.field_view?.contract_version === "source-fields.v1" ? props.record.field_view : undefined
  const data = createMemo(() => displayedExtractionFields(fieldView()?.data ?? props.record.result_json))
  const sourceData = createMemo(() => ({
    ...sourceSupportingFields(props.record.result_json),
    ...(fieldView()?.data.source ? { source: fieldView()!.data.source } : {}),
  }))
  const accountingRecords = createMemo(() => {
    const value = data()
    const normalized = isObject(value.normalized) ? value.normalized : {}
    const tables = Array.isArray(value.tables) ? value.tables : []
    const table = tables.find((item) => isObject(item) && item.name === "accounting_records")
    const rows = value.accounting_records ?? normalized.accounting_records ?? (isObject(table) ? table.rows : undefined)
    return Array.isArray(rows) ? rows.filter(isObject) : []
  })
  const documentFields = createMemo(() => {
    const value = data().document_fields
    return isObject(value) ? value : undefined
  })
  const accounting = () =>
    accountingRecords().length > 0 ||
    ["invoice", "receipt", "bank_statement", "payroll"].includes(String(documentFields()?.document_type)) ||
    /accounting|invoice|receipt/i.test(props.record.schema_name ?? "") ||
    String(data().source ?? "").includes("accounting") ||
    isObject(data().invoice) ||
    (isObject(data().normalized) && isObject((data().normalized as Record<string, unknown>).invoice))
  return (
    <ExtractionFieldLabels labels={fieldView()?.labels}>
      <Show when={props.record.schema_name === "file.extracted_data"} fallback={
      <Show when={accounting()} fallback={<ExtractedValue value={data()} />}>
        <Show
          when={accountingRecords().length > 0}
          fallback={
            <Show
              when={fieldView()}
              fallback={
                <Suspense fallback={<p role="status">{language.t("cowork.files.panel.loadingPreview")}</p>}>
                  <AccountingExtractionData record={props.record} files={props.files} />
                </Suspense>
              }
            >
              <ExtractedValue value={data()} />
            </Show>
          }
        >
          <AccountingRecordFacts records={accountingRecords()} />
        </Show>
        <Show when={Object.keys(sourceData()).length > 0}>
          <details class="mt-4 rounded-lg border border-v2-border-border-base p-3">
            <summary class="cursor-pointer font-medium">
              {language.t("cowork.accounting.extractions.sourceFields")}
            </summary>
            <div class="mt-3">
              <ExtractedValue value={sourceData()} discloseSupporting={false} />
            </div>
          </details>
        </Show>
      </Show>
      }>
        <ExtractedValue value={data()} />
      </Show>
    </ExtractionFieldLabels>
  )
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
}
